/**
 * AI에게 전송할 형태로 페이지 정보를 변환하는 유틸리티
 */

import { PageElement, ElementNode, getPageContext } from './pageContext';

// AI 포맷 결과 타입
export interface AIFormattedPageContext {
  context: string;
  elementMappings: ElementMapping[];
}

/**
 * AI에게 전송할 형태로 포맷된 페이지 컨텍스트를 반환합니다.
 * 인덱스 기반으로 요소를 참조하고, 매핑 정보도 함께 반환합니다.
 */
export const aiFormattedPageContext = (): AIFormattedPageContext => {
  const pageContext = getPageContext();
  const lines: string[] = [];
  const elementMappings: ElementMapping[] = [];
  let currentIndex = 0;

  // 페이지 기본 정보
  lines.push(`# Page Information`);
  lines.push(`Path: ${pageContext.pathname}`);

  // Format Guide 먼저 제공
  lines.push(`\n# Format Guide`);
  lines.push(`Structure: symbol[index]<description>{value}`);
  lines.push(
    `
    Example:
    *[0]<로그인 버튼>
    +[1]<사용자 이메일>{user@example.com}
    [2]<제목 텍스트>
    `.trim(),
  );
  lines.push(``);
  lines.push(`Symbols:`);
  lines.push(`* = clickable (buttons, links)`);
  lines.push(`+ = input (text fields, textareas)`);
  lines.push(`> = select (dropdowns)`);
  lines.push(`@ = selectable (checkboxes, radios)`);
  lines.push(`(no symbol) = non-interactive (display only)`);
  lines.push(`\\t = child element (indented)`);

  // 인터랙티브 요소들 (계층 구조 유지)
  lines.push(`\n# Interactive Elements`);
  lines.push(``);

  // 재귀적으로 요소들을 문자열로 변환
  const processNode = (node: ElementNode, depth: number) => {
    const element = node.element;

    // selector가 없으면 스킵 (안전장치)
    if (!element.selector) {
      // 자식 요소들은 여전히 처리
      node.children.forEach((child) => {
        processNode(child, depth);
      });
      return;
    }

    const selector = element.selector;
    const elementIndex = currentIndex++;

    // 매핑 정보 저장
    elementMappings.push({
      index: elementIndex,
      selector: selector,
    });

    // 인터랙션 심볼 확인
    const symbol = getInteractionSymbol(element);

    // AI 설명 생성 - data-description이 있을 때만 사용
    let aiDescription = '';
    if (element.description) {
      // 개행과 연속된 공백을 하나의 공백으로 변경
      aiDescription = element.description
        .replace(/\s+/g, ' ') // 모든 공백문자(개행, 탭, 공백)를 하나의 공백으로
        .trim();
    }

    // 현재 값이 있으면 표시 (간결하게)
    let valueDisplay = '';
    if (element.value) {
      valueDisplay = `{${element.value.trim()}}`;
    }

    // 들여쓰기 (탭으로 계층 표시)
    let indent = '';
    for (let j = 0; j < depth; j++) {
      indent += '\t';
    }

    // 인덱스 기반 형태: symbol[index]<description>{value}
    lines.push(`${indent}${symbol}[${elementIndex}]<${aiDescription}>${valueDisplay}`);

    // 자식 요소들 처리
    node.children.forEach((child) => {
      processNode(child, depth + 1);
    });
  };

  // 루트 레벨 요소들 처리
  pageContext.elementTree.children.forEach((child) => {
    processNode(child, 0);
  });

  return {
    context: lines.join('\n'),
    elementMappings,
  };
};

/**
 * 요소의 인터랙션 타입 심볼을 결정합니다.
 */
const getInteractionSymbol = (element: PageElement): string => {
  const tag = element.tag.toLowerCase();
  const type = element.type?.toLowerCase();

  // 클릭 가능한 요소들
  if (tag === 'button' || tag === 'a' || element.role === 'button') {
    return '*'; // clickable
  }

  // 입력 가능한 요소들
  if (tag === 'input') {
    if (type === 'checkbox' || type === 'radio') {
      return '@'; // selectable
    }
    if (type === 'submit' || type === 'button') {
      return '*'; // clickable
    }
    return '+'; // input
  }

  if (tag === 'textarea') {
    return '+'; // input
  }

  if (tag === 'select') {
    return '>'; // select
  }

  // 편집 가능한 div
  if (tag === 'div' && element.role === 'textbox') {
    return '+'; // input
  }

  // 기타 클릭 가능한 역할
  if (element.role === 'tab' || element.role === 'menuitem') {
    return '*'; // clickable
  }

  // 기본적으로 인터랙티브하지 않은 요소
  return ''; // non-interactive
};

// 인덱스와 ID 매핑 타입
export interface ElementMapping {
  index: number;
  selector: string;
}

/**
 * 인덱스를 ID로 변환하는 함수
 */
export const getSelectorFromIndex = (index: number, mappings: ElementMapping[]): string | null => {
  for (let i = 0; i < mappings.length; i++) {
    if (mappings[i].index === index) {
      return mappings[i].selector;
    }
  }
  return null;
};
